// client/src/views/Interactive_parts/InteractiveMapEffects.js
import { useEffect } from 'react';
import { getPersonalPinKey } from './PinUtils.js';

function useZoneHoverGlow(
  normalizedHoveredZoneId,
  shapeLayerMap,
  prevOriginalStylesRef,
  animationRef,
  glowOnRef
) {
  useEffect(() => {
    if (animationRef.current) {
      clearTimeout(animationRef.current);
      animationRef.current = null;
    }

    const effectiveId =
      normalizedHoveredZoneId != null ? normalizedHoveredZoneId : null;

    if (effectiveId == null || !shapeLayerMap.current[effectiveId]) {
      Object.entries(prevOriginalStylesRef.current).forEach(([id, style]) => {
        const layer = shapeLayerMap.current[id];
        if (layer && layer.setStyle) layer.setStyle(style);
        if (layer && layer._path) layer._path.style.filter = '';
      });
      prevOriginalStylesRef.current = {};
    } else {
      const layer = shapeLayerMap.current[effectiveId];
      if (!layer || !layer.setStyle) return;

      const meta = layer._shapeMeta || {};
      const behaviorType =
        meta.behaviorType ?? meta.behavior_type ?? null;

      if (behaviorType === 'pathing') {
        return;
      }

      if (!prevOriginalStylesRef.current[effectiveId]) {
        prevOriginalStylesRef.current[effectiveId] = {
          color: layer.options.color,
          fillColor: layer.options.fillColor,
          opacity: layer.options.opacity,
          fillOpacity: layer.options.fillOpacity,
          weight: layer.options.weight,
          dashArray: layer.options.dashArray || ''
        };
      }

      const cycleGlow = () => {
        if (!layer || !layer.setStyle) return;
        if (glowOnRef.current) {
          layer.setStyle({
            color: '#fff',
            fillColor: '#fff',
            opacity: 0.95,
            fillOpacity: 0.27,
            weight: 5,
            dashArray: '4 4'
          });
          if (layer._path) {
            layer._path.style.filter =
              'drop-shadow(0 0 6px #fff) drop-shadow(0 0 14px #fff)';
            layer._path.style.transition =
              'filter 0.5s, stroke 0.5s, fill 0.5s';
          }
        } else {
          const style = prevOriginalStylesRef.current[effectiveId];
          layer.setStyle(style);
          if (layer._path) layer._path.style.filter = '';
        }
        glowOnRef.current = !glowOnRef.current;
        animationRef.current = setTimeout(cycleGlow, 1000);
      };

      cycleGlow();
    }

    return () => {
      if (animationRef.current) {
        clearTimeout(animationRef.current);
        animationRef.current = null;
      }
      const effectiveIdCleanup =
        normalizedHoveredZoneId != null ? normalizedHoveredZoneId : null;
      if (
        effectiveIdCleanup != null &&
        shapeLayerMap.current[effectiveIdCleanup] &&
        prevOriginalStylesRef.current[effectiveIdCleanup]
      ) {
        const layer = shapeLayerMap.current[effectiveIdCleanup];
        layer.setStyle(prevOriginalStylesRef.current[effectiveIdCleanup]);
        if (layer._path) layer._path.style.filter = '';
        delete prevOriginalStylesRef.current[effectiveIdCleanup];
      }
    };
  }, [normalizedHoveredZoneId]);
}

function useMapCreateGuard(
  mapDivRef,
  hoveredPoiId,
  setHoveredPoiId,
  canCreateHereRef,
  setCanCreateHere
) {
  useEffect(() => {
    const el = mapDivRef.current;
    if (!el) return;

    function isBlockedTarget(target) {
      const blocked = target.closest(
        '.leaflet-interactive, .leaflet-marker-icon, .leaflet-popup, .leaflet-control, .pm-toolbar, .pm-action, .pm-draw, .pm-control, .shape-edit-toolbar, .map-controls-cluster, .embed-popup, .personal-pin-modal, .poi-creation-popup, .poi-info-modal, .poi-edit-modal, .poi-modal-backdrop'
      );
      return !!blocked;
    }

    function handleMouseMove(e) {
      const t = e.target;
      if (
        !t.closest('.leaflet-marker-icon') &&
        !t.closest('.leaflet-popup')
      ) {
        if (hoveredPoiId !== null) setHoveredPoiId(null);
      }
      const nextCanCreate = !isBlockedTarget(t);
      if (canCreateHereRef.current !== nextCanCreate) {
        canCreateHereRef.current = nextCanCreate;
        setCanCreateHere(nextCanCreate);
      }
    }

    function handleMouseLeave() {
      if (canCreateHereRef.current !== false) {
        canCreateHereRef.current = false;
        setCanCreateHere(false);
      }
    }

    el.addEventListener('mousemove', handleMouseMove);
    el.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      el.removeEventListener('mousemove', handleMouseMove);
      el.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [hoveredPoiId, setHoveredPoiId]);
}

function usePersonalPinsStorage(
  mapId,
  personalPins,
  setPersonalPins,
  pinsLoaded,
  setPinsLoaded
) {
  useEffect(() => {
    if (!mapId) return;
    const key = getPersonalPinKey(mapId);
    const pins = JSON.parse(localStorage.getItem(key) || '[]');
    setPersonalPins(Array.isArray(pins) ? pins : []);
    setPinsLoaded(true);
  }, [mapId]);

  useEffect(() => {
    if (!mapId || !pinsLoaded) return;
    const key = getPersonalPinKey(mapId);
    localStorage.setItem(key, JSON.stringify(personalPins));
  }, [personalPins, mapId, pinsLoaded]);
}

export {
  useZoneHoverGlow,
  useMapCreateGuard,
  usePersonalPinsStorage,
};
